import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileSearch,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DiscriminatedTreatmentRequestResearchCompletedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface TreatmentRequestResearchCompletedEventProps {
  event: DiscriminatedTreatmentRequestResearchCompletedEvent;
  matterId: string;
}

export const TreatmentRequestResearchCompletedEvent: React.FC<
  TreatmentRequestResearchCompletedEventProps
> = ({ event, matterId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { provider_id, submission_methods, selected_submission_methods } =
    payload;

  const hasSubmissionMethods =
    submission_methods && submission_methods.length > 0;
  const hasSelectedMethods =
    selected_submission_methods && selected_submission_methods.length > 0;

  return (
    <EventContainer>
      <EventHeader
        icon={<FileSearch size={20} />}
        title="Treatment Request Research Completed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Summary */}
          <div className="text-xs">
            <span className="text-slate-600">Found</span>{' '}
            <span className="font-medium">
              {submission_methods?.length || 0} submission method
              {submission_methods?.length !== 1 ? 's' : ''}
            </span>
            {hasSelectedMethods && (
              <>
                {' '}
                <span className="text-slate-600">(</span>
                <span className="font-medium text-green-700">
                  {selected_submission_methods.length} selected
                </span>
                <span className="text-slate-600">)</span>
              </>
            )}
          </div>

          {/* Selected Submission Methods */}
          {hasSelectedMethods && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium text-slate-700">
                Selected Methods:
              </div>
              {selected_submission_methods.map((method, index) => (
                <div
                  key={index}
                  className="text-[11px] space-y-0.5 pl-2 border-l-2 border-green-500"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="default">
                      {formatSubmissionMethodType(method.submission_method)}
                    </Badge>
                    {method.confidence && (
                      <span className="text-slate-500">
                        {Math.round(method.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                  {method.reason && (
                    <div className="text-slate-600">{method.reason}</div>
                  )}
                  {method.scope && method.scope.length > 0 && (
                    <div className="text-slate-500">
                      Scope: {method.scope.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Collapsible All Submission Methods */}
          {hasSubmissionMethods && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} all submission methods</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-2 border-l-2 border-slate-200 pl-2.5">
                {submission_methods.map((method, index) => {
                  const isSelected = selected_submission_methods?.some(
                    (selected) =>
                      JSON.stringify(selected) === JSON.stringify(method),
                  );

                  return (
                    <div key={index} className="text-[11px] space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Badge variant={isSelected ? 'default' : 'outline'}>
                          {formatSubmissionMethodType(method.submission_method)}
                        </Badge>
                        {method.confidence && (
                          <span className="text-slate-500">
                            {Math.round(method.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                      {method.reason && (
                        <div className="text-slate-600">{method.reason}</div>
                      )}
                      {method.scope && method.scope.length > 0 && (
                        <div className="text-slate-500">
                          Scope: {method.scope.join(', ')}
                        </div>
                      )}
                      {method.submission_instructions && (
                        <div className="text-slate-500 italic">
                          {method.submission_instructions}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment?provider_id=${provider_id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Provider
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatSubmissionMethodType(method: any): string {
  if (!method) return 'Unknown';

  // Extract channel type from the submission method object
  if (method.channel_type) {
    const channelType = method.channel_type;
    const types: Record<string, string> = {
      PORTAL: 'Portal',
      FAX: 'Fax',
      EMAIL: 'Email',
      MAILING_ADDRESS: 'Mail',
      WEBSITE: 'Website',
    };
    return types[channelType] || channelType;
  }

  return 'Unknown';
}
